import moment from 'moment'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Calendar,
  Check,
  Download,
  Edit,
  Loader2,
  Play,
  Trash,
  X,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import type { ProgramRecord } from '../program.types'

type EditNameState = { id: string; displayName: string } | null

type Props = {
  file: ProgramRecord
  isInDialog?: boolean
  isUpdating: boolean
  editName: EditNameState
  onEditNameChange: (next: EditNameState) => void
  onUpdateFile: (
    id: string,
    fileUrl: string,
    isEnabled?: boolean,
    displayName?: string,
  ) => Promise<unknown>
  onPreview: (file: ProgramRecord) => void
  onRequestDelete: (file: ProgramRecord) => void
}

export default function VideoFileCard({
  file,
  isInDialog,
  isUpdating,
  editName,
  onEditNameChange,
  onUpdateFile,
  onPreview,
  onRequestDelete,
}: Props) {
  const isEditing = editName?.id === file.id
  const enabledLabelId = `file-enabled-${file.id}`

  const commitRename = async (next: string) => {
    await onUpdateFile(file.id, file.fileUrl, undefined, next)
    onEditNameChange(null)
  }

  // Compact (non-dialog) variant
  if (!isInDialog) {
    return (
      <div className="mb-3 flex items-center gap-3 rounded-xl border bg-white p-3 transition-colors hover:bg-gray-50">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50">
          <Play className="h-4 w-4 text-blue-600" />
        </div>
        <div className="min-w-0 flex-1">
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="truncate text-sm font-medium text-gray-900">
                  {file.displayName}
                </p>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[80vw] break-all">
                <p>{file.displayName}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <p className="truncate text-xs text-gray-500 tabular-nums">
            {moment(file.createdAt).format('DD/MM/yyyy HH:mm')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <article
      className={cn(
        'group relative mb-3 overflow-hidden rounded-xl border bg-white',
        'transition-shadow duration-200 hover:shadow-md focus-within:shadow-md',
      )}
    >
      {/* Subtle updating overlay */}
      {isUpdating && (
        <div className="pointer-events-none absolute right-3 top-3 z-10 rounded-full bg-white/90 p-1.5 shadow-sm ring-1 ring-gray-200">
          <Loader2 className="h-4 w-4 animate-spin text-gray-700" />
        </div>
      )}

      {/* Thumbnail with overlay metadata */}
      <button
        type="button"
        onClick={() => onPreview(file)}
        className={cn(
          'relative block aspect-video w-full overflow-hidden bg-gray-900',
          'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        )}
        aria-label={`เล่นพรีวิว ${file.displayName}`}
      >
        <video
          src={file.fileUrl}
          className={cn(
            'h-full w-full object-cover',
            'motion-safe:transition-transform motion-safe:duration-300 motion-safe:group-hover:scale-[1.02]',
          )}
          controls={false}
          preload="metadata"
          tabIndex={-1}
          onClick={(e) => e.preventDefault()}
        />

        {/* Top gradient for badge legibility */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/50 to-transparent"
          aria-hidden="true"
        />
        {/* Bottom gradient for date legibility */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/70 to-transparent"
          aria-hidden="true"
        />

        {/* Enabled status badge */}
        <div className="absolute left-2 top-2">
          <Badge
            variant={file.isEnabled ? 'default' : 'secondary'}
            className={cn(
              'gap-1 px-2 py-0.5 text-[11px] font-medium backdrop-blur-sm',
              file.isEnabled
                ? 'bg-emerald-500/90 text-white hover:bg-emerald-500/90'
                : 'bg-gray-700/80 text-gray-100 hover:bg-gray-700/80',
            )}
          >
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                file.isEnabled ? 'bg-white' : 'bg-gray-300',
              )}
              aria-hidden="true"
            />
            {file.isEnabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
          </Badge>
        </div>

        {/* Play affordance center */}
        <span className="absolute inset-0 flex items-center justify-center">
          <span
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-full bg-black/55 backdrop-blur-sm ring-1 ring-white/20',
              'motion-safe:transition-transform motion-safe:duration-200 motion-safe:group-hover:scale-110',
            )}
          >
            <Play className="h-6 w-6 fill-white text-white" strokeWidth={1} />
          </span>
        </span>

        {/* Date overlay bottom-left */}
        <div className="absolute bottom-2 left-2 flex items-center gap-1.5 text-xs text-white/95">
          <Calendar className="h-3.5 w-3.5" />
          <time className="tabular-nums">
            {moment(file.createdAt).format('DD/MM/yyyy HH:mm')}
          </time>
        </div>
      </button>

      {/* Content */}
      <div className="flex flex-col gap-3 p-3 sm:p-4">
        {/* Filename + edit toggle */}
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            {isEditing ? (
              <Input
                value={editName.displayName}
                onChange={(e) =>
                  onEditNameChange({
                    ...editName,
                    displayName: e.target.value,
                  })
                }
                className="h-9 text-base font-medium"
                autoFocus
                disabled={isUpdating}
                placeholder="ตั้งชื่อไฟล์"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    commitRename((e.target as HTMLInputElement).value)
                  }
                  if (e.key === 'Escape') {
                    onEditNameChange(null)
                  }
                }}
                aria-label="ชื่อไฟล์"
              />
            ) : (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h3 className="truncate text-base font-semibold leading-tight text-gray-900">
                      {file.displayName}
                    </h3>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[80vw] break-all"
                  >
                    <p>{file.displayName}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            {isEditing && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="default"
                      size="icon"
                      className="h-9 w-9"
                      disabled={isUpdating}
                      onClick={() => commitRename(editName.displayName)}
                      aria-label="บันทึก"
                    >
                      {isUpdating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>บันทึก</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant={isEditing ? 'ghost' : 'outline'}
                    size="icon"
                    className="h-9 w-9"
                    disabled={isUpdating}
                    onClick={() =>
                      isEditing
                        ? onEditNameChange(null)
                        : onEditNameChange({
                            id: file.id,
                            displayName: file.displayName,
                          })
                    }
                    aria-label={isEditing ? 'ยกเลิก' : 'แก้ไขชื่อ'}
                  >
                    {isEditing ? (
                      <X className="h-4 w-4" />
                    ) : (
                      <Edit className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isEditing ? 'ยกเลิก' : 'แก้ไขชื่อ'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Controls row: enable switch + actions (destructive separated) */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={!!file.isEnabled}
              onCheckedChange={(e) => onUpdateFile(file.id, file.fileUrl, e)}
              disabled={isUpdating}
              id={enabledLabelId}
              aria-label="เปิดหรือปิดใช้งานไฟล์"
            />
            <Label
              htmlFor={enabledLabelId}
              className="cursor-pointer select-none text-sm font-medium text-gray-700"
            >
              {file.isEnabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
            </Label>
          </div>

          <div className="flex items-center gap-1">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => window.open(file.fileUrl, '_blank')}
                    aria-label="ดาวน์โหลดไฟล์"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>ดาวน์โหลดไฟล์</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <span
              className="mx-1 h-6 w-px bg-border"
              aria-hidden="true"
            />

            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'h-9 w-9 text-destructive',
                      'hover:bg-destructive/10 hover:text-destructive',
                      'focus-visible:ring-destructive',
                    )}
                    disabled={isUpdating}
                    onClick={() => {
                      if (!isUpdating) onRequestDelete(file)
                    }}
                    aria-label="ลบไฟล์"
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>ลบไฟล์</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </article>
  )
}
